// Auswahlliste der Schadenmappe (Risiko, Vertrag): Suchfeld, Zeilen mit Haupt-
// und Nebenzeile, die gewählte Zeile markiert, begrenzt auf eine Seite mit
// Blättern. Gefiltert und geblättert wird im Browser — die Endpunkte liefern
// die Risiken/Verträge eines Kontakts bzw. Risikos vollständig.
import * as React from "react";

export interface AuswahlEintrag {
  id: number;
  label: string;
  /** Nebenzeile (Anschrift, Kennzeichen, Sparte · Nummer · Versicherer …). */
  neben?: string;
}

export const AUSWAHL_SEITE = 8;

export interface AuswahlListeProps {
  /** `null` = lädt. */
  eintraege: AuswahlEintrag[] | null;
  gewaehltId: number | null;
  /** Klick auf eine Zeile wählt sie; Klick auf die gewählte hebt die Wahl auf (`null`). */
  onWahl: (e: AuswahlEintrag | null) => void;
  /** Zugänglicher Name von Suchfeld und Liste, z. B. „Risiko“. */
  name: string;
  platzhalter: string;
  /** Text, wenn es gar keine Einträge gibt. */
  leer: React.ReactNode;
}

export function AuswahlListe({ eintraege, gewaehltId, onWahl, name, platzhalter, leer }: AuswahlListeProps) {
  const [suche, setSuche] = React.useState("");
  const [seite, setSeite] = React.useState(0);

  const gefiltert = React.useMemo(() => {
    const worte = suche.toLowerCase().split(/\s+/).filter(Boolean);
    const alle = eintraege ?? [];
    if (worte.length === 0) return alle;
    return alle.filter((e) => {
      const text = `${e.label} ${e.neben ?? ""}`.toLowerCase();
      return worte.every((w) => text.includes(w));
    });
  }, [eintraege, suche]);

  if (eintraege === null) return <div className="smp-hinweis">lädt …</div>;
  if (eintraege.length === 0) return <div className="smp-auswahl-leer">{leer}</div>;

  const seiten = Math.max(1, Math.ceil(gefiltert.length / AUSWAHL_SEITE));
  const aktuelle = Math.min(seite, seiten - 1);
  const von = aktuelle * AUSWAHL_SEITE;
  const sichtbar = gefiltert.slice(von, von + AUSWAHL_SEITE);

  return (
    <div className="smp-auswahl">
      {eintraege.length > 1 && (
        <input
          type="search"
          className="smp-suche-feld"
          aria-label={`${name} suchen`}
          placeholder={platzhalter}
          value={suche}
          onChange={(e) => {
            setSuche(e.target.value);
            setSeite(0);
          }}
        />
      )}
      {sichtbar.length === 0 ? (
        <div className="smp-hinweis">Keine Treffer für „{suche.trim()}“.</div>
      ) : (
        <ul className="smp-auswahl-liste" aria-label={name}>
          {sichtbar.map((e) => {
            const aktiv = e.id === gewaehltId;
            return (
              <li key={e.id}>
                <button
                  type="button"
                  className={`smp-auswahl-zeile${aktiv ? " ist-aktiv" : ""}`}
                  aria-pressed={aktiv}
                  onClick={() => onWahl(aktiv ? null : e)}
                >
                  <span className="smp-auswahl-marke" aria-hidden="true">{aktiv ? "✓" : ""}</span>
                  <span className="smp-auswahl-text">
                    <span className="smp-auswahl-name">{e.label}</span>
                    {e.neben ? <span className="smp-auswahl-neben">{e.neben}</span> : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {gefiltert.length > AUSWAHL_SEITE && (
        <div className="smp-auswahl-fuss">
          <span className="smp-hinweis">
            {von + 1}–{von + sichtbar.length} von {gefiltert.length}
          </span>
          <span className="smp-auswahl-blaettern">
            <button
              type="button"
              className="tbtn"
              aria-label={`${name}: vorherige Seite`}
              disabled={aktuelle === 0}
              onClick={() => setSeite(aktuelle - 1)}
            >
              ‹ Vorherige
            </button>
            <button
              type="button"
              className="tbtn"
              aria-label={`${name}: nächste Seite`}
              disabled={aktuelle >= seiten - 1}
              onClick={() => setSeite(aktuelle + 1)}
            >
              Nächste ›
            </button>
          </span>
        </div>
      )}
    </div>
  );
}
